import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { AD_OBJECTIVES, statusBadgeClasses, getAdDisplayLabel, daysBetween, formatSubmittedAt } from "./adsConstants";
import successIcon from "../../assets/images/icons/ads-submitted-success.svg";

export default function AdSubmittedSuccess() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const ad = state?.ad;
  const campaign = state?.campaign;
  const adAccount = state?.adAccount;
  const coverImage = ad?.creative_media?.[0]?.url;
  const objective = AD_OBJECTIVES.find((o) => o.value === campaign?.objective);
  const duration = campaign ? daysBetween(campaign.start_date, campaign.end_date) : null;
  const dailyBudget = campaign?.budget && duration ? (Number(campaign.budget) / duration).toFixed(2) : null;

  if (!ad || !campaign) {
    navigate("/ads", { replace: true });
    return null;
  }

  return (
    // Exact layout from the Figma "Ad Campaign Submitted Successfully" screen (read-only
    // access, matched from a shared screenshot): a dimmed full-page backdrop with a centered
    // modal card, rather than a plain routed page - kept as a route for simplicity, but
    // styled to be visually identical to the modal shown in Figma.
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50 overflow-y-auto">
      <div className="bg-white rounded-[24px] w-full max-w-3xl p-8 relative my-auto">
        <div className="flex items-center justify-end mb-6">
          <button
            type="button"
            onClick={() => navigate("/ads")}
            className="w-8 h-8 rounded-full bg-[#095228] text-white flex items-center justify-center shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        <div className="text-center">
          <img src={successIcon} alt="" className="w-[130px] h-[130px] mx-auto mb-2" />
          <h1 className="text-xl font-bold mb-1">
            Ad Campaign
            <br />
            Submitted Successfully!
          </h1>
          <p className="text-sm text-[#747474] mb-6">
            Your ad is currently in review. Our team typically processes approvals within 1-2 hours.
          </p>
        </div>

        <div className="rounded-2xl p-4 mb-6 bg-[#fefdeb] text-left">
          {coverImage && (
            ad.creative_media?.[0]?.media_type === "video" ? (
              <video src={coverImage} controls className="w-full h-40 object-cover rounded-xl mb-3" />
            ) : (
              <img src={coverImage} alt="" className="w-full h-40 object-cover rounded-xl mb-3" />
            )
          )}
          <div className="flex items-center gap-2 mb-3">
            <span className={`text-xs font-semibold uppercase px-2 py-0.5 rounded-full ${statusBadgeClasses(ad.status)}`}>
              {getAdDisplayLabel(ad.status)}
            </span>
            <span className="text-xs text-[#747474]">ID: AD-{ad.id}</span>
          </div>

          {objective && (
            <div className="flex items-center gap-2 mb-2">
              {objective.iconSrc && <img src={objective.iconSrc} alt="" className="w-8 h-8" />}
              <p className="font-semibold text-[#131b2e]">{objective.label}</p>
            </div>
          )}

          <p className="text-xs font-semibold text-orange-600 uppercase mb-1">{ad.headline || ad.name}</p>
          <p className="text-sm text-[#434656] mb-3">{ad.body_text}</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t text-sm">
            <div>
              <p className="text-[#747474] text-xs">Total Budget</p>
              <p className="font-semibold">$ {campaign.budget}</p>
            </div>
            <div>
              <p className="text-[#747474] text-xs">Daily Budget</p>
              <p className="font-semibold">$ {dailyBudget ?? "-"}</p>
            </div>
            <div>
              <p className="text-[#747474] text-xs">Duration</p>
              <p className="font-semibold">{duration ? `${duration} Days` : "-"}</p>
            </div>
            <div>
              <p className="text-[#747474] text-xs">Schedule</p>
              <p className="font-semibold">
                {campaign.start_date} - {campaign.end_date}
              </p>
            </div>
          </div>
          <p className="text-xs text-[#9a9a9a] mt-3 pt-3 border-t">
            Submitted {formatSubmittedAt(ad.updated_at || ad.created_at)}
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate("/ads")}
          className="w-full bg-[#095228] text-white font-semibold py-3 rounded-full mb-3"
        >
          Go to Ad Manager
        </button>
        <button
          type="button"
          onClick={() => navigate("/ads/create", { state: { adAccount } })}
          className="w-full bg-[#ebf8ed] border border-[#095228] text-[#095228] font-semibold py-3 rounded-full"
        >
          Create Another Ad
        </button>
      </div>
    </div>
  );
}
